import { ChangeDetectionStrategy, Component, Input } from "@angular/core";

/**
 * Los iconos propios de las salidas: el PDF (un documento con su doblez y la etiqueta «PDF»), SonarQube (abrir
 * aparte), la hoja del Excel y el anillo que gira mientras se emite.
 */
export type NombreIconoCc = "pdf" | "sonar" | "hoja" | "girando";

/**
 * Un icono de trazo fino (1.5 en un lienzo de 24, redondeado) en currentColor: toma la tinta del botón que lo lleva,
 * en los tres temas. 20 × 20 px; quien lo usa lo cambia con --cc-icono-tam. Es decorativo (aria-hidden): el nombre
 * lo dice el botón (12/10/2026, pedido de Norman: «mejórame este icono»). El anillo gira con transform (900 ms,
 * lineal y sin fin, al mismo paso que el icono de «Actualizar»); con prefers-reduced-motion se queda quieto.
 */
@Component({
  selector: "app-icono-cc",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "cc-icono", "aria-hidden": "true", "[attr.data-icono]": "nombre" },
  template: `
    <svg viewBox="0 0 24 24" focusable="false" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
         stroke-linejoin="round">
      @switch (nombre) {
        @case ("pdf") {
          <path d="M5.5 11.5V4.75A1.75 1.75 0 0 1 7.25 3h6.5l4.75 4.75v3.75"></path>
          <path d="M13.75 3v3.5A1.25 1.25 0 0 0 15 7.75h3.5"></path>
          <path d="M4.75 21v-6h1.75a1.75 1.75 0 0 1 0 3.5H4.75"></path>
          <path d="M10.5 15v6h1a2.25 2.25 0 0 0 2.25-2.25v-1.5A2.25 2.25 0 0 0 11.5 15z"></path>
          <path d="M16 21v-6h3.25M16 18h2.5"></path>
        }
        @case ("sonar") {
          <path d="M10.5 4.75H7A2.25 2.25 0 0 0 4.75 7v10A2.25 2.25 0 0 0 7 19.25h10A2.25 2.25 0 0 0 19.25 17v-3.5"></path>
          <path d="M14 4.75h5.25V10"></path>
          <path d="M19.25 4.75l-8.5 8.5"></path>
        }
        @case ("hoja") {
          <rect x="4.75" y="4.75" width="14.5" height="14.5" rx="2.25"></rect>
          <path d="M4.75 9.75h14.5M4.75 14.5h14.5M10 9.75v9.5"></path>
        }
        @case ("girando") {
          <circle class="cc-icono__pista" cx="12" cy="12" r="8"></circle>
          <path class="cc-icono__arco" d="M12 4a8 8 0 0 1 8 8"></path>
        }
      }
    </svg>
  `,
  styles: `
    :host {
      display: inline-flex;
      flex: 0 0 auto;
      height: var(--cc-icono-tam, 20px);
      width: var(--cc-icono-tam, 20px);
    }

    svg {
      display: block;
      height: 100%;
      overflow: visible;
      width: 100%;
    }

    /* La pista del anillo, tenue: el arco en la tinta entera es lo que se mueve. */
    .cc-icono__pista {
      opacity: 0.22;
    }

    @media (prefers-reduced-motion: no-preference) {
      :host([data-icono="girando"]) svg {
        animation: cc-icono-gira 900ms linear infinite;
      }
    }

    @keyframes cc-icono-gira {
      to {
        transform: rotate(360deg);
      }
    }
  `,
})
export class IconoCcComponent {
  @Input() nombre: NombreIconoCc = "pdf";
}
